/**
 * 메뉴 허용 트리.
 * 기본 체크는 지금 화면에 보이는 것과 같다.
 * id 는 backend/app/services/brand_menu_service.py 와 같아야 한다.
 */
export type Grade = 'super' | 'manager' | 'facility';

export interface MenuItem {
  id: string;
  label: string;
  parentId: string | null;
  defaults: Record<Grade, boolean>;
}

export type GrantRow = Record<Grade, boolean>;
export type Grants = Record<string, GrantRow>;

const S: GrantRow = { super: true, manager: false, facility: false };
const SM: GrantRow = { super: true, manager: true, facility: false };
const ALL: GrantRow = { super: true, manager: true, facility: true };

export const GRADES: Grade[] = ['super', 'manager', 'facility'];

export const MENU_ITEMS: MenuItem[] = [
  { id: 'nav.dashboard', label: '대시보드', parentId: null, defaults: S },
  { id: 'nav.central-monitoring', label: '통합 관제 모니터링', parentId: null, defaults: S },
  { id: 'nav.individual', label: '개별 관제 모니터링', parentId: null, defaults: SM },
  { id: 'nav.messages', label: '관제 메시지', parentId: null, defaults: SM },
  { id: 'nav.facility-board', label: '시설 게시판', parentId: null, defaults: S },
  { id: 'nav.archive', label: '관제 자료실', parentId: null, defaults: S },
  { id: 'nav.pools', label: '시설 관리', parentId: null, defaults: S },
  { id: 'nav.pools.list', label: '시설 목록', parentId: 'nav.pools', defaults: S },
  { id: 'nav.pools.status', label: '시설 상태', parentId: 'nav.pools', defaults: S },
  { id: 'nav.system', label: '시스템 관리', parentId: null, defaults: S },
  { id: 'system.basic', label: '기본 설정', parentId: 'nav.system', defaults: S },
  { id: 'system.security', label: '보안', parentId: 'nav.system', defaults: S },
  { id: 'system.passwords', label: '비밀번호 관리', parentId: 'nav.system', defaults: S },
  { id: 'system.admins', label: '관리자 관리', parentId: 'nav.system', defaults: S },
  { id: 'system.wearables', label: '웨어러블 관리', parentId: 'nav.system', defaults: S },
  { id: 'system.pool-passwords', label: '시설 비밀번호', parentId: 'nav.system', defaults: S },
  { id: 'system.data', label: '데이터 관리', parentId: 'nav.system', defaults: S },
  { id: 'system.data.auto-backup', label: '자동 백업 허용', parentId: 'system.data', defaults: S },
  { id: 'system.alerts', label: '알림', parentId: 'nav.system', defaults: S },
  { id: 'system.alerts.share-image', label: '합성 이미지 서비스', parentId: 'system.alerts', defaults: S },
  { id: 'system.alerts.kakao-bot', label: '카카오 봇 보내기', parentId: 'system.alerts', defaults: S },
  { id: 'system.monitoring', label: '모니터링', parentId: 'nav.system', defaults: S },
  { id: 'system.api', label: 'API 관리', parentId: 'nav.system', defaults: S },
  { id: 'system.benchmark', label: '운영·벤치마크', parentId: 'nav.system', defaults: S },
  { id: 'system.thresholds', label: '임계 설정', parentId: 'nav.system', defaults: S },
  { id: 'system.labels', label: '검출 라벨 · 학습', parentId: 'nav.system', defaults: S },
  { id: 'nav.reports', label: '리포트', parentId: null, defaults: S },
  { id: 'report.overview', label: '운영 요약', parentId: 'nav.reports', defaults: S },
  { id: 'report.events', label: '이벤트 발생 내역', parentId: 'nav.reports', defaults: S },
  { id: 'report.rollup', label: '이벤트·위험 집계', parentId: 'nav.reports', defaults: S },
  { id: 'report.alerts', label: '알림 발송', parentId: 'nav.reports', defaults: S },
  { id: 'report.fp', label: '오탐', parentId: 'nav.reports', defaults: S },
  { id: 'report.busy', label: '혼잡 시간', parentId: 'nav.reports', defaults: S },
  { id: 'nav.camera-layout', label: '카메라 배치·설치', parentId: null, defaults: S },
  { id: 'nav.camera-spec', label: '카메라 시방 자동 배치', parentId: null, defaults: S },
  { id: 'nav.label-recording', label: '영상 녹화', parentId: null, defaults: S },
  { id: 'nav.physiological', label: '생체 모니터링', parentId: null, defaults: S },
  { id: 'facility.root', label: '시설 화면', parentId: null, defaults: ALL },
  { id: 'facility.events', label: '관제 이벤트', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.info', label: '시설 정보', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.cctv', label: 'CCTV 관리', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.siren', label: '경광등/부저', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.siren-a', label: '경광등/부저 A', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.wearable', label: '웨어러블 관리', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.members', label: '회원 관리', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.monitoring', label: '모니터링', parentId: 'facility.root', defaults: ALL },
  { id: 'mon.live', label: '실시간 모니터링', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.cctv', label: 'CCTV', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.water', label: '수질 정보', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.safety', label: '안전 상태', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.sensor', label: '센서', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.alerts', label: '알림', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.wearable', label: '웨어러블', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'mon.settings', label: '환경 설정', parentId: 'facility.monitoring', defaults: ALL },
  { id: 'facility.reports', label: '리포트', parentId: 'facility.root', defaults: ALL },
  { id: 'freport.overview', label: '운영 요약', parentId: 'facility.reports', defaults: ALL },
  { id: 'freport.events', label: '이벤트', parentId: 'facility.reports', defaults: ALL },
  { id: 'freport.wearable', label: '웨어러블', parentId: 'facility.reports', defaults: ALL },
  { id: 'freport.members', label: '회원 활동', parentId: 'facility.reports', defaults: ALL },
  { id: 'freport.busy', label: '혼잡·알림', parentId: 'facility.reports', defaults: ALL },
  { id: 'freport.water', label: '수질·안전', parentId: 'facility.reports', defaults: ALL },
  { id: 'facility.board', label: '시설 게시판', parentId: 'facility.root', defaults: ALL },
  { id: 'facility.settings', label: '시설 환경 설정', parentId: 'facility.root', defaults: ALL },
  { id: 'fset.water', label: '수질 기준', parentId: 'facility.settings', defaults: ALL },
  { id: 'fset.safety', label: '안전 기준', parentId: 'facility.settings', defaults: ALL },
  { id: 'fset.alerts', label: '알림 설정', parentId: 'facility.settings', defaults: ALL },
  { id: 'fset.auto', label: '자동 제어', parentId: 'facility.settings', defaults: ALL },
  { id: 'fset.schedule', label: '운영 일정', parentId: 'facility.settings', defaults: ALL },
  { id: 'fset.camera', label: '카메라 배치', parentId: 'facility.settings', defaults: ALL },
];

const BY_ID = new Map(MENU_ITEMS.map((item) => [item.id, item]));

export const SYSTEM_TAB_IDS = [
  'system.basic',
  'system.security',
  'system.passwords',
  'system.admins',
  'system.wearables',
  'system.pool-passwords',
  'system.data',
  'system.alerts',
  'system.monitoring',
  'system.api',
  'system.benchmark',
  'system.thresholds',
  'system.labels',
] as const;

export const POOL_MGMT_TAB_IDS = [
  'facility.events',
  'facility.info',
  'facility.cctv',
  'facility.siren',
  'facility.siren-a',
  'facility.wearable',
  'facility.members',
  'facility.monitoring',
  'facility.reports',
  'facility.board',
] as const;

export const MONITOR_TAB_IDS = [
  'mon.live',
  'mon.cctv',
  'mon.water',
  'mon.safety',
  'mon.sensor',
  'mon.alerts',
  'mon.wearable',
  'mon.settings',
] as const;

export const CENTRAL_REPORT_TAB_IDS = [
  'report.overview',
  'report.events',
  'report.rollup',
  'report.alerts',
  'report.fp',
  'report.busy',
] as const;

export const POOL_REPORT_TAB_IDS = [
  'freport.overview',
  'freport.events',
  'freport.wearable',
  'freport.members',
  'freport.busy',
  'freport.water',
] as const;

export const POOL_SETTINGS_TAB_IDS = [
  'fset.water',
  'fset.safety',
  'fset.alerts',
  'fset.auto',
  'fset.schedule',
  'fset.camera',
] as const;

export function isFacilityItem(id: string): boolean {
  return id.startsWith('facility.') || id.startsWith('mon.') || id.startsWith('freport.') || id.startsWith('fset.');
}

export function defaultGrants(): Grants {
  const out: Grants = {};
  MENU_ITEMS.forEach((item) => {
    out[item.id] = { ...item.defaults };
  });
  return out;
}

function imply(row: GrantRow): void {
  if (row.facility) {
    row.manager = true;
    row.super = true;
  } else if (row.manager) {
    row.super = true;
  }
  if (!row.super) {
    row.manager = false;
    row.facility = false;
  } else if (!row.manager) {
    row.facility = false;
  }
}

export function normalizeGrants(grants: Grants): Grants {
  const out: Grants = {};
  MENU_ITEMS.forEach((item) => {
    const src = grants[item.id] || {};
    const row: GrantRow = { ...item.defaults };
    GRADES.forEach((grade) => {
      if (grade in src) row[grade] = Boolean(src[grade]);
    });
    imply(row);
    out[item.id] = row;
  });

  let changed = true;
  while (changed) {
    changed = false;
    MENU_ITEMS.forEach((item) => {
      if (!item.parentId || !out[item.parentId]) return;
      const parent = out[item.parentId];
      const cur = out[item.id];
      GRADES.forEach((grade) => {
        if (!parent[grade] && cur[grade]) {
          cur[grade] = false;
          changed = true;
        }
      });
      const before = { ...cur };
      imply(cur);
      if (before.super !== cur.super || before.manager !== cur.manager || before.facility !== cur.facility) {
        changed = true;
      }
    });
  }
  return out;
}

function childrenIds(parentId: string): string[] {
  return MENU_ITEMS.filter((item) => item.parentId === parentId).map((item) => item.id);
}

function descendantIds(id: string): string[] {
  const found: string[] = [];
  const stack = childrenIds(id);
  while (stack.length) {
    const cur = stack.pop() as string;
    found.push(cur);
    stack.push(...childrenIds(cur));
  }
  return found;
}

function ancestorIds(id: string): string[] {
  const found: string[] = [];
  let cur = BY_ID.get(id);
  const seen = new Set<string>();
  while (cur?.parentId && !seen.has(cur.parentId)) {
    seen.add(cur.parentId);
    found.push(cur.parentId);
    cur = BY_ID.get(cur.parentId);
  }
  return found;
}

export function applyToggle(grants: Grants, id: string, grade: Grade, checked: boolean): Grants {
  if (!BY_ID.has(id)) return normalizeGrants(grants);
  const next = normalizeGrants(grants);
  const idx = GRADES.indexOf(grade);
  if (checked) {
    [id, ...ancestorIds(id)].forEach((target) => {
      GRADES.slice(0, idx + 1).forEach((g) => {
        next[target][g] = true;
      });
    });
  } else {
    [id, ...descendantIds(id)].forEach((target) => {
      GRADES.slice(idx).forEach((g) => {
        next[target][g] = false;
      });
    });
  }
  return normalizeGrants(next);
}

export function menuDepth(id: string): number {
  let depth = 0;
  let cur = BY_ID.get(id);
  const seen = new Set<string>();
  while (cur?.parentId && !seen.has(cur.parentId) && depth < 8) {
    seen.add(cur.parentId);
    depth += 1;
    cur = BY_ID.get(cur.parentId);
  }
  return depth;
}

export function isGranted(id: string, grade: Grade, server: Grants | null): boolean {
  const row = (server && server[id]) || BY_ID.get(id)?.defaults;
  if (!row) return true;
  return row[grade] !== false;
}

export function viewerGrade(user: { is_superuser?: boolean; is_brand_manager?: boolean } | null | undefined, authenticated: boolean): Grade | 'brand' {
  if (user?.is_brand_manager) return 'brand';
  if (authenticated && user?.is_superuser) return 'super';
  if (authenticated) return 'manager';
  return 'facility';
}

/** 관제 메시지 레이아웃처럼 원래 등급 구분 없이 보이던 링크는, 기본이 꺼진 항목을 숨기지 않는다. */
export function shellKeepsLink(menuId: string | null, grade: Grade | 'brand', granted: boolean): boolean {
  if (!menuId) return true;
  if (grade === 'brand') return true;
  const defaults = BY_ID.get(menuId)?.defaults;
  if (!defaults) return true;
  if (defaults[grade] === false) return true;
  return granted;
}

export function matchMenuId(pathname: string, search: string): string | null {
  const path = pathname.replace(/\/$/, '') || '/';
  const tab = new URLSearchParams(search).get('tab') || '';
  if (path === '/settings') return 'nav.system';
  if (path === '/central-control') {
    switch (tab) {
      case '':
      case 'dashboard':
        return 'nav.dashboard';
      case 'pools':
        return 'nav.pools.list';
      case 'pool-status':
        return 'nav.pools.status';
      case 'system':
        return 'nav.system';
      case 'reports':
        return 'nav.reports';
      case 'messages':
        return 'nav.messages';
      case 'facility-board':
        return 'nav.facility-board';
      case 'control-archive':
        return 'nav.archive';
      case 'camera-layout':
        return 'nav.camera-layout';
      case 'camera-spec-wizard':
        return 'nav.camera-spec';
      case 'label-recording':
        return 'nav.label-recording';
      default:
        return 'nav.dashboard';
    }
  }
  if (path === '/dashboard') return 'nav.dashboard';
  if (path === '/individual-monitoring') return 'nav.individual';
  if (path === '/operator-messages') return 'nav.messages';
  if (path === '/physiological-monitoring') return 'nav.physiological';
  if (path === '/all/cctv' || path === '/central-monitoring' || path === '/cctv-fullscreen' || path === '/main-monitoring') {
    return 'nav.central-monitoring';
  }
  if (path === '/pool-management' || path.startsWith('/pool-management/')) return 'nav.pools';
  return null;
}

export function isAdminPathDenied(
  pathname: string,
  search: string,
  grade: Grade,
  granted: (id: string) => boolean,
): boolean {
  const id = matchMenuId(pathname, search);
  if (!id) return false;
  return !granted(id);
}

const FALLBACKS: { id: string; path: string }[] = [
  { id: 'nav.individual', path: '/individual-monitoring' },
  { id: 'nav.messages', path: '/operator-messages' },
  { id: 'nav.dashboard', path: '/central-control' },
  { id: 'nav.system', path: '/central-control?tab=system' },
  { id: 'nav.pools', path: '/central-control?tab=pools' },
  { id: 'nav.reports', path: '/central-control?tab=reports' },
  { id: 'nav.physiological', path: '/physiological-monitoring' },
];

function sameTarget(pathname: string, search: string, target: string): boolean {
  const url = new URL(target, 'http://local');
  const curTab = new URLSearchParams(search).get('tab') || '';
  const nextTab = url.searchParams.get('tab') || '';
  const path = pathname.replace(/\/$/, '') || '/';
  const nextPath = url.pathname.replace(/\/$/, '') || '/';
  return path === nextPath && curTab === nextTab;
}

export function nextAllowedAdminPath(
  pathname: string,
  search: string,
  granted: (id: string) => boolean,
): string | null {
  for (const item of FALLBACKS) {
    if (!granted(item.id)) continue;
    if (sameTarget(pathname, search, item.path)) continue;
    return item.path;
  }
  return null;
}

export function hiddenTabProps(visible: boolean): { hidden?: boolean; sx?: { display: 'none' } } {
  if (visible) return {};
  return { hidden: true, sx: { display: 'none' } };
}

export function selfCheck(): { item: string; ok: boolean; detail: string }[] {
  const results: { item: string; ok: boolean; detail: string }[] = [];
  const add = (item: string, ok: boolean, detail = '') => results.push({ item, ok, detail });
  const ids = MENU_ITEMS.map((item) => item.id);
  add('메뉴 id 중복 없음', new Set(ids).size === ids.length, `${ids.length}개`);
  const missing = MENU_ITEMS.filter((item) => item.parentId && !BY_ID.has(item.parentId)).map((item) => item.id);
  add('상위 메뉴 존재', missing.length === 0, missing.join(','));
  const bad: string[] = [];
  MENU_ITEMS.forEach((item) => {
    const fac = isFacilityItem(item.id);
    if (!item.defaults.super) bad.push(item.id);
    if (fac) {
      if (!item.defaults.manager || !item.defaults.facility) bad.push(item.id);
    } else if (item.defaults.facility) {
      bad.push(`${item.id}:facility`);
    } else if (item.defaults.manager !== (item.id === 'nav.individual' || item.id === 'nav.messages')) {
      bad.push(`${item.id}:manager`);
    }
  });
  add('기본 체크=현재 화면', bad.length === 0, bad.join(','));
  const normalized = normalizeGrants(defaultGrants());
  const defaults = defaultGrants();
  const drifted = Object.keys(defaults).filter((id) => {
    const a = defaults[id];
    const b = normalized[id];
    return a.super !== b.super || a.manager !== b.manager || a.facility !== b.facility;
  });
  add('기본값 보정 후에도 동일', drifted.length === 0, drifted.join(','));
  const deniedDash = isAdminPathDenied('/central-control', '', 'super', (id) => isGranted(id, 'super', null));
  add('최고관리자 대시보드 기본 허용', deniedDash === false);
  const deniedManagerHome = isAdminPathDenied('/individual-monitoring', '', 'manager', (id) => isGranted(id, 'manager', null));
  add('일반관리자 개별 관제 기본 허용', deniedManagerHome === false);
  const facilityPath = matchMenuId('/jeju-dongbu', '');
  add('시설 주소는 메뉴 차단 대상이 아님', facilityPath === null);
  const legacy = ['nav.dashboard', 'nav.individual', 'nav.messages', 'nav.pools', 'nav.system'].every((id) =>
    shellKeepsLink(id, 'manager', isGranted(id, 'manager', null)),
  );
  add('일반관리자 기존 메시지 화면 링크 유지', legacy);
  return results;
}
